import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { DockviewApi, DockviewGroupPanel } from 'dockview';

interface TabDefinition {
  id: string;
  title: string;
  component: string;
  description?: string;
}

interface TabPickerProps {
  open: boolean;
  onClose: () => void;
  api: DockviewApi | null;
  availableTabs: TabDefinition[];
  targetGroup?: DockviewGroupPanel | null;
}

export const TabPicker: React.FC<TabPickerProps> = ({ open, onClose, api, availableTabs, targetGroup }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter tabs based on search query
  const filteredTabs = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableTabs;
    }
    const query = searchQuery.toLowerCase();
    return availableTabs.filter(
      (tab) =>
        tab.title.toLowerCase().includes(query) ||
        tab.description?.toLowerCase().includes(query)
    );
  }, [searchQuery, availableTabs]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedIndex(0);
      // Focus input after a short delay to ensure dialog is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filteredTabs.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (filteredTabs[selectedIndex]) {
        handleSelectTab(filteredTabs[selectedIndex]);
      }
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  // Handle tab selection
  const handleSelectTab = (tab: TabDefinition) => {
    if (!api) return;

    // Check if panel already exists
    const existingPanel = api.getPanel(tab.id);
    if (existingPanel) {
      // If it exists, just activate it
      existingPanel.api.setActive();
    } else {
      // Use the target group (from button click) or fall back to active group
      const groupToUse = targetGroup || api.activeGroup;

      // Create new panel in the target group
      const panel = api.addPanel({
        id: tab.id,
        component: tab.component,
        title: tab.title,
        position: groupToUse ? { referenceGroup: groupToUse } : undefined,
      });
      panel.api.setActive();
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disablePortal
      sx={{
        position: 'absolute',
        height: '100%',
        width: '100%',
      }}
      BackdropProps={{
        sx: {
          position: 'absolute',
          height: '100%',
          width: '100%',
        },
      }}
      PaperProps={{
        sx: {
          position: 'absolute',
          top: '20%',
          m: 0,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Search panels..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                '& fieldset': {
                  border: 'none',
                  borderBottom: 1,
                  borderColor: 'divider',
                  borderRadius: 0,
                },
              },
            }}
          />
          <List sx={{ maxHeight: '400px', overflow: 'auto', py: 0 }}>
            {filteredTabs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No panels found
                </Typography>
              </Box>
            ) : (
              filteredTabs.map((tab, index) => (
                <ListItemButton
                  key={tab.id}
                  selected={index === selectedIndex}
                  onClick={() => handleSelectTab(tab)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  sx={{
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <ListItemText
                    primary={tab.title}
                    secondary={tab.description}
                    primaryTypographyProps={{
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
